import React, { FC } from 'react';
import { useLingui } from '@lingui/react';
import { FeatureItemProps, RenderComponentProps } from './types';
import { Box, Text, ToggleSwitch, TextField, Checkbox } from '@bechtel/gds';
import { Trans } from '@lingui/macro';
import { useFormikContext } from 'formik';
import { ErrorBoundary } from '@bechtel/client-common';
import { FormControlStyled } from './styled';

const ErrorField = ({ error }) => {
  const { i18n } = useLingui();
  return (
    <Box pt={{ sm: '200' }}>
      <Text color="bechtel.red">{i18n._(error)}</Text>
    </Box>
  );
};

const TextComponent: FC<RenderComponentProps> = ({ metaProperty, index, propertyKey }) => {
  const { values, setFieldValue, errors } = useFormikContext();
  const errorMessage = errors?.features ? errors?.features[index]?.metaData?.properties[propertyKey]?.value : null;
  return (
    <Box key={index} pl={{ sm: '400' }} pr={{ sm: '200' }} flexDirection={{ sm: 'column' }}>
      <FormControlStyled label={metaProperty?.displayText} isEnabled={values?.features[index]?.enabled}>
        <TextField
          defaultValue={metaProperty?.value ? metaProperty.value : ''}
          type={metaProperty?.type ? metaProperty.type : null}
          onChange={(e) => {
            setFieldValue(`features[${index}].metaData.properties[${propertyKey}].value`, e.target.value);
          }}
        />
      </FormControlStyled>
      {errorMessage ? <ErrorField error={errorMessage} /> : null}
    </Box>
  );
};

const CheckboxComponent: FC<RenderComponentProps> = ({ metaProperty, index, propertyKey }) => {
  const { values, setFieldValue } = useFormikContext();
  return (
    <Box
      key={index}
      css={!values?.features[index]?.enabled ? 'opacity:0.5; pointer-events:none' : ''}
      px={{ sm: '400' }}
    >
      <Box alignItems={{ sm: 'center' }}>
        <Checkbox
          checked={values?.features[index]?.metaData?.properties[propertyKey]?.value}
          onChange={(e) => {
            setFieldValue(`features[${index}].metaData.properties[${propertyKey}].value`, e.target.checked);
          }}
        />
      </Box>
      <Box m={{ sm: '200' }} alignItems={{ sm: 'center' }}>
        <Text family="graphik" size={300}>
          {metaProperty?.displayText}
        </Text>
      </Box>
    </Box>
  );
};

const NumberComponent: FC<RenderComponentProps> = ({ metaProperty, index, propertyKey }) => {
  const { values, setFieldValue, errors } = useFormikContext();
  const errorMessage = errors?.features ? errors?.features[index]?.metaData?.properties[propertyKey]?.value : null;
  return (
    <Box key={index}>
      <Box pr={{ sm: '200' }} flexDirection={{ sm: 'column' }}>
        <FormControlStyled label={metaProperty?.displayText} isEnabled={values?.features[index]?.enabled}>
          <TextField
            defaultValue={metaProperty?.value ? metaProperty.value : ''}
            min={metaProperty?.min ? metaProperty.min : null}
            max={metaProperty?.max ? metaProperty.max : null}
            type={metaProperty?.type ? metaProperty.type : null}
            onChange={(e) => {
              setFieldValue(`features[${index}].metaData.properties[${propertyKey}].value`, e.target.value);
            }}
          />
        </FormControlStyled>
        {errorMessage ? <ErrorField error={errorMessage} /> : null}
      </Box>
      {values?.features[index]?.enabled && (
        <Box
          flexDirection={{ sm: 'column' }}
          pr={{ sm: '400' }}
          justifyContent={{ sm: 'left' }}
          alignSelf={{ sm: 'center' }}
        >
          {metaProperty && metaProperty?.min && (
            <Box alignItems={{ sm: 'center' }}>
              <Text family="graphik" color="bechtel.red" size="200">
                <Trans>Min:</Trans>
              </Text>
              &nbsp;
              <Text family="graphik" color="bechtel.red" size="200" weight="600">
                {metaProperty.min}
              </Text>
            </Box>
          )}
          {metaProperty && metaProperty?.max && (
            <Box alignItems={{ sm: 'center' }}>
              <Text family="graphik" color="bechtel.red" size="200">
                <Trans>Max:</Trans>
              </Text>
              &nbsp;
              <Text family="graphik" color="bechtel.red" size="200" weight="600">
                {metaProperty.max}
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const FieldMetaData = ({ propertyKey, index, properties }) => {
  switch (properties[propertyKey]?.type?.toLowerCase()) {
    case 'text':
      return (
        <ErrorBoundary>
          <TextComponent metaProperty={properties[propertyKey]} index={index} propertyKey={propertyKey} />
        </ErrorBoundary>
      );
    case 'number':
      return (
        <ErrorBoundary>
          <NumberComponent metaProperty={properties[propertyKey]} index={index} propertyKey={propertyKey} />
        </ErrorBoundary>
      );
    case 'checkbox':
      return (
        <ErrorBoundary>
          <CheckboxComponent metaProperty={properties[propertyKey]} index={index} propertyKey={propertyKey} />
        </ErrorBoundary>
      );
    default:
      return (
        <ErrorBoundary>
          <TextComponent metaProperty={properties[propertyKey]} index={index} propertyKey={propertyKey} />
        </ErrorBoundary>
      );
  }
};

export const FeatureItem: FC<FeatureItemProps> = ({ featureItem, index }) => {
  const { setFieldValue, values } = useFormikContext();

  return (
    <Box
      data-testid={`feature-item-${index}`}
      alignItems={{ sm: 'center' }}
      justifyContent={{ sm: 'space-between' }}
      py={{ sm: '500' }}
      css={`
        border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
        height: 68px;
      `}
      key={index}
    >
      <Box flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '300' }}>
          <Text family="graphik" color="grey.1000" size="300">
            {featureItem?.featureDefinition?.name ? featureItem?.featureDefinition?.name : ''}
          </Text>
        </Box>
        <Text family="graphik" color="grey.600" size="300">
          {featureItem.featureDefinition?.description}
        </Text>
      </Box>
      <Box alignItems={{ sm: 'center' }}>
        {featureItem?.metaData?.properties && (
          <Box alignItems={{ sm: 'center' }}>
            {Object.keys(featureItem?.metaData?.properties).map((key, metaIndex) => {
              return (
                <FieldMetaData
                  propertyKey={key}
                  index={index}
                  properties={featureItem?.metaData?.properties}
                  key={metaIndex}
                />
              );
            })}
          </Box>
        )}
        <Box mr={{ sm: '400' }} ml={{ sm: '800' }}>
          <ToggleSwitch
            isToggled={values?.features[index]?.enabled}
            disabled={values?.features[index]?.readOnly}
            onToggle={() => {
              setFieldValue(`features[${index}].enabled`, !values?.features[index]?.enabled);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
